import { useState, useEffect } from 'react'
import { StatusBadge, STATUS_LABELS } from '@/components/common'
import type { Document, DocumentStatus } from '@/types'
import { DOCUMENT_TYPE_LABELS, getDocumentTypeLabel } from '@/types'
import type { DocumentType } from '@/types'

interface MetadataFormProps {
  document: Document
  onSave: (data: Partial<Document>) => void
  isSaving: boolean
}

const DOCUMENT_TYPES = Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]

const DOCUMENT_STATUSES: DocumentStatus[] = [
  'new',
  'processing',
  'extracted',
  'reported',
  'invalid',
  'manual_review',
]

export function MetadataForm({ document, onSave, isSaving }: MetadataFormProps) {
  const [formData, setFormData] = useState<Partial<Document>>({})
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    setFormData({
      document_number: document.document_number,
      issued_by: document.issued_by,
      issued_to: document.issued_to,
      value: document.value,
      vat_value: document.vat_value,
      document_type: document.document_type,
      document_status: document.document_status,
    })
  }, [document])

  const handleChange = (field: string, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setFormData({
      document_number: document.document_number,
      issued_by: document.issued_by,
      issued_to: document.issued_to,
      value: document.value,
      vat_value: document.vat_value,
      document_type: document.document_type,
      document_status: document.document_status,
    })
    setIsEditing(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="metadata-form">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Document Details</h3>
        {!isEditing ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
            data-testid="edit-btn"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="text-sm text-gray-600 hover:text-gray-800"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              data-testid="save-btn"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-4">
        {/* Read-only fields */}
        <div>
          <dt className="text-sm font-medium text-gray-500">Document Date</dt>
          <dd className="mt-1 text-sm text-gray-900">{document.document_date}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Status</dt>
          <dd className="mt-1">
            {isEditing ? (
              <select
                value={formData.document_status || ''}
                onChange={(e) => handleChange('document_status', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="status-select"
              >
                {DOCUMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            ) : (
              <StatusBadge status={document.document_status} />
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Document Number</dt>
          <dd className="mt-1">
            {isEditing ? (
              <input
                type="text"
                value={formData.document_number || ''}
                onChange={(e) => handleChange('document_number', e.target.value || null)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="document-number-input"
              />
            ) : (
              <span className="text-sm text-gray-900">
                {document.document_number || '-'}
              </span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Document Type</dt>
          <dd className="mt-1">
            {isEditing ? (
              <select
                value={formData.document_type || ''}
                onChange={(e) => handleChange('document_type', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="document-type-select"
              >
                {DOCUMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {DOCUMENT_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm text-gray-900">
                {getDocumentTypeLabel(document.document_type, document.document_type_id)}
              </span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Issued By</dt>
          <dd className="mt-1">
            {isEditing ? (
              <input
                type="text"
                value={formData.issued_by || ''}
                onChange={(e) => handleChange('issued_by', e.target.value || null)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="issued-by-input"
              />
            ) : (
              <span className="text-sm text-gray-900">{document.issued_by || '-'}</span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Issued To</dt>
          <dd className="mt-1">
            {isEditing ? (
              <input
                type="text"
                value={formData.issued_to || ''}
                onChange={(e) => handleChange('issued_to', e.target.value || null)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="issued-to-input"
              />
            ) : (
              <span className="text-sm text-gray-900">{document.issued_to || '-'}</span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Value</dt>
          <dd className="mt-1">
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                value={formData.value ?? ''}
                onChange={(e) =>
                  handleChange('value', e.target.value ? parseFloat(e.target.value) : null)
                }
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="value-input"
              />
            ) : (
              <span className="text-sm text-gray-900">
                {document.value !== null
                  ? `${Number(document.value).toFixed(2)} ${document.original_currency}`
                  : '-'}
              </span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">VAT Value</dt>
          <dd className="mt-1">
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                value={formData.vat_value ?? ''}
                onChange={(e) =>
                  handleChange('vat_value', e.target.value ? parseFloat(e.target.value) : null)
                }
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                data-testid="vat-value-input"
              />
            ) : (
              <span className="text-sm text-gray-900">
                {document.vat_value !== null
                  ? `${Number(document.vat_value).toFixed(2)} ${document.original_currency}`
                  : '-'}
              </span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Currency</dt>
          <dd className="mt-1 text-sm text-gray-900">{document.original_currency}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Source</dt>
          <dd className="mt-1 text-sm text-gray-900 capitalize">{document.source}</dd>
        </div>

        <div className="col-span-2">
          <dt className="text-sm font-medium text-gray-500">S3 Key</dt>
          <dd className="mt-1 text-sm text-gray-500 font-mono text-xs break-all">
            {document.s3_key}
          </dd>
        </div>
      </dl>
    </form>
  )
}
